import React, { useState, useEffect, useRef } from 'react';
import './trans.css';
import logo from './img/edaplogo.png';
import unilislogo from './img/unilislogo.png';
import rectorSig from './img/rector_sig.png';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Download } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm } from 'antd';
import { getDatabase, ref, set, remove } from 'firebase/database';
import app from './firebase.js';
import QRCode from 'qrcode';

const SUMMARY_FIELDS = [
  'name',
  'matriculationNumber',
  'class',
  'faculty',
  'department',
  'year',
  'verdict',
  'programType',
  'date',
  'semesters',
  'generalTotalCredits',
  'generalTotalGrades',
  'generalTotalGPA'
];

const fetchTranscriptField = async (rootUrl, key, field) => {
  const response = await fetch(`${rootUrl}/${encodeURIComponent(key)}/${field}.json`);
  if (!response.ok) {
    throw new Error(`Failed to load ${field}`);
  }
  return response.json();
};

const fetchTranscriptSummary = async (rootUrl, key) => {
  const entries = await Promise.all(
    SUMMARY_FIELDS.map(async (field) => [field, await fetchTranscriptField(rootUrl, key, field)])
  );
  const data = Object.fromEntries(entries);
  return {
    id: key,
    ...data,
    matriculationNumber: data.matriculationNumber || key
  };
};

const Transcript = () => {
  const [formData, setFormData] = useState({
    name: '',
    matriculationNumber: '',
    class: '',
    faculty: '',
    department: '',
    year: '',
    image: null,
    verdict: '',
    programType: '',
    date: '',
    semesters: [{ 
      semesterNumber: 'I', 
      courses: [{ code: '', title: '', titleEn: '', credit: '', grade: '', gpa: '' }],
      totalCredits: '',
      totalGrades: '',
      totalGPA: ''
    }],
    generalTotalCredits: '',
    generalTotalGrades: '',
    generalTotalGPA: ''
  });

  const [transcripts, setTranscripts] = useState([]);
  const [students, setStudents] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMatricule, setEditingMatricule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(null);
  const [listError, setListError] = useState('');
  const [isModalLoading, setIsModalLoading] = useState(false);
  const hiddenIdsRef = useRef(new Set());

  const db = getDatabase(app);
  const transcriptRootUrl = ref(db, 'transcripts/UNCR').toString();

  useEffect(() => {
    let cancelled = false;

    const loadTranscripts = async () => {
      setIsLoadingList(true);
      setLoadedCount(0);
      setTotalCount(null);
      setListError('');

      try {
        const response = await fetch(`${transcriptRootUrl}.json?shallow=true`);
        if (!response.ok) {
          throw new Error('Failed to load bulletin list');
        }
        const keysMap = await response.json();
        const keys = keysMap ? Object.keys(keysMap) : [];
        if (cancelled) return;

        setTotalCount(keys.length);
        if (keys.length === 0) {
          setIsLoadingList(false);
          return;
        }

        let nextIndex = 0;
        const workers = Array.from({ length: Math.min(4, keys.length) }, async () => {
          while (nextIndex < keys.length) {
            const key = keys[nextIndex];
            nextIndex += 1;
            try {
              const summary = await fetchTranscriptSummary(transcriptRootUrl, key);
              if (cancelled || hiddenIdsRef.current.has(summary.id)) continue;
              setTranscripts((prev) => (
                prev.some((item) => item.id === summary.id) ? prev : [...prev, summary]
              ));
            } catch (error) {
              console.error(`Error loading bulletin ${key}:`, error);
            } finally {
              if (!cancelled) {
                setLoadedCount((count) => count + 1);
              }
            }
          }
        });

        await Promise.all(workers);
      } catch (error) {
        console.error('Error loading bulletins:', error);
        if (!cancelled) {
          setListError('Impossible de charger les bulletins.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingList(false);
        }
      }
    };

    loadTranscripts();
    return () => {
      cancelled = true;
    };
  }, [transcriptRootUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCourseChange = (semesterIndex, courseIndex, e) => {
    const { name, value } = e.target;
    const updatedSemesters = [...formData.semesters];
    const updatedCourses = [...updatedSemesters[semesterIndex].courses];
    updatedCourses[courseIndex][name] = value;
    updatedSemesters[semesterIndex].courses = updatedCourses;
    setFormData((prevData) => ({
      ...prevData,
      semesters: updatedSemesters,
    }));
  };

  const handleSemesterTotalChange = (semesterIndex, e) => {
    const { name, value } = e.target;
    const updatedSemesters = [...formData.semesters];
    updatedSemesters[semesterIndex][name] = value;
    setFormData((prevData) => ({
      ...prevData,
      semesters: updatedSemesters,
    }));
  };

  const addCourse = (semesterIndex) => {
    const updatedSemesters = [...formData.semesters];
    updatedSemesters[semesterIndex].courses.push({ 
      code: '', 
      title: '', 
      titleEn: '',
      credit: '', 
      grade: '', 
      gpa: '' 
    });
    setFormData((prevData) => ({
      ...prevData,
      semesters: updatedSemesters,
    }));
  };

  // Helper function to capitalize Roman numerals
  const capitalizeRomanNumeral = (str) => {
    if (!str) return '';
    return str.trim().toUpperCase();
  };

  const addSemester = () => {
    const semesterNumber = prompt('Entrez le numéro du semestre (ex: I, II, III, IV):', '');
    if (semesterNumber) {
      const capitalizedNumber = capitalizeRomanNumeral(semesterNumber);
      setFormData((prevData) => ({
        ...prevData,
      semesters: [...prevData.semesters, {
        semesterNumber: capitalizedNumber,
        courses: [{ code: '', title: '', titleEn: '', credit: '', grade: '', gpa: '' }],
        totalCredits: '',
        totalGrades: '',
        totalGPA: ''
      }],
      }));
    }
  };

  const removeSemester = (semesterIndex) => {
    if (formData.semesters.length > 1) {
      const updatedSemesters = formData.semesters.filter((_, index) => index !== semesterIndex);
      setFormData((prevData) => ({
        ...prevData,
        semesters: updatedSemesters,
      }));
    } else {
      alert('Vous devez avoir au moins un semestre');
    }
  };

  const removeCourse = (semesterIndex, courseIndex) => {
    const updatedSemesters = [...formData.semesters];
    if (updatedSemesters[semesterIndex].courses.length > 1) {
      updatedSemesters[semesterIndex].courses = updatedSemesters[semesterIndex].courses.filter(
        (_, index) => index !== courseIndex
      );
      setFormData((prevData) => ({
        ...prevData,
        semesters: updatedSemesters,
      }));
    } else {
      alert('Vous devez avoir au moins un cours par semestre');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prevData) => ({
          ...prevData,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let image = formData.image;
    if (!image && formData.matriculationNumber) {
      try {
        image = await fetchTranscriptField(transcriptRootUrl, formData.matriculationNumber, 'image');
      } catch (error) {
        console.error('Error loading student photo:', error);
      }
    }

    const newTranscript = {
      name: formData.name,
      matriculationNumber: formData.matriculationNumber,
      class: formData.class,
      faculty: formData.faculty,
      department: formData.department,
      year: formData.year,
      image,
      verdict: formData.verdict,
      programType: formData.programType,
      date: formData.date,
      semesters: formData.semesters,
      generalTotalCredits: formData.generalTotalCredits,
      generalTotalGrades: formData.generalTotalGrades,
      generalTotalGPA: formData.generalTotalGPA
    };

    // Save or update transcript in Firebase
    const saveTranscript = async () => {
      const targetKey = formData.matriculationNumber;
      if (isEditing && editingMatricule && editingMatricule !== targetKey) {
        await remove(ref(db, `/transcripts/UNCR/${editingMatricule}`));
      }
      await set(ref(db, `/transcripts/UNCR/${targetKey}`), newTranscript);
    };

    saveTranscript()
      .then(() => {
        const saved = {
          id: formData.matriculationNumber,
          ...newTranscript
        };
        setTranscripts((prev) => {
          const withoutReplaced = prev.filter((item) => (
            item.id !== saved.id &&
            item.matriculationNumber !== saved.matriculationNumber &&
            !(isEditing && editingMatricule && (item.id === editingMatricule || item.matriculationNumber === editingMatricule))
          ));
          return [...withoutReplaced, saved];
        });
        console.log(isEditing ? 'Transcript updated successfully' : 'Transcript saved successfully in Firebase');
        resetForm();
      })
      .catch((error) => console.error('Error saving transcript:', error));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      matriculationNumber: '',
      class: '',
      faculty: '',
      department: '',
      year: '',
      image: null,
      verdict: '',
      programType: '',
      date: '',
      semesters: [{ 
        semesterNumber: 'I', 
        courses: [{ code: '', title: '', titleEn: '', credit: '', grade: '', gpa: '' }],
        totalCredits: '',
        totalGrades: '',
        totalGPA: ''
      }],
      generalTotalCredits: '',
      generalTotalGrades: '',
      generalTotalGPA: ''
    });
    setIsEditing(false);
    setEditingMatricule(null);
  };

  const handleEditTranscript = (transcript) => {
    // Convert old format (courses) to new format (semesters) if needed
    let semesters = transcript.semesters;
    if (!semesters && transcript.courses) {
      // Migrate old format to new format
      semesters = [{
        semesterNumber: 'I',
        courses: transcript.courses
      }];
    }
    if (!semesters || semesters.length === 0) {
      semesters = [{
        semesterNumber: 'I',
        courses: []
      }];
    }
    
    // Capitalize all semester numbers
    semesters = semesters.map(sem => ({
      ...sem,
      semesterNumber: capitalizeRomanNumeral(sem.semesterNumber)
    }));
    
    setFormData({
      name: transcript.name || '',
      matriculationNumber: transcript.matriculationNumber || '',
      class: transcript.class || '',
      faculty: transcript.faculty || '',
      department: transcript.department || '',
      year: transcript.year || '',
      image: transcript.image || null,
      verdict: transcript.verdict || '',
      programType: transcript.programType || '',
      date: transcript.date || '',
      semesters: semesters || [{ 
        semesterNumber: 'I', 
        courses: [{ code: '', title: '', titleEn: '', credit: '', grade: '', gpa: '' }],
        totalCredits: '',
        totalGrades: '',
        totalGPA: ''
      }],
      generalTotalCredits: transcript.generalTotalCredits || '',
      generalTotalGrades: transcript.generalTotalGrades || '',
      generalTotalGPA: transcript.generalTotalGPA || ''
    });
    setIsEditing(true);
    setEditingMatricule(transcript.matriculationNumber || transcript.id);

    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!transcript.image && (transcript.id || transcript.matriculationNumber)) {
      const photoKey = transcript.id || transcript.matriculationNumber;
      fetchTranscriptField(transcriptRootUrl, photoKey, 'image')
        .then((image) => {
          if (!image) return;
          setFormData((prev) => (
            prev.matriculationNumber === (transcript.matriculationNumber || transcript.id)
              ? { ...prev, image }
              : prev
          ));
          setTranscripts((prev) => prev.map((item) => (
            item.id === photoKey ? { ...item, image } : item
          )));
        })
        .catch((error) => console.error('Error loading student photo:', error));
    }
  };

  // Helper to generate a single page (French or English)
  const generatePage = (data, lang, qrCodeData) => {
    const isFr = lang === 'fr';
    
    // Get program type display
    let programTypeDisplay = '';
    if (['Master', 'Doctorat', 'Licence', 'HDR'].includes(data.programType)) {
      programTypeDisplay = data.programType;
    }
    
    // Format date
    let formattedDate = '';
    if (data.date) {
      try {
        const dateObj = new Date(data.date);
        if (!isNaN(dateObj.getTime())) {
          const day = String(dateObj.getDate()).padStart(2, '0');
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const year = dateObj.getFullYear();
          formattedDate = isFr
            ? `Lisala, le ${day}/${month}/${year}`
            : `Lisala, ${day}/${month}/${year}`;
        }
      } catch (e) {
        console.error('Date formatting error:', e);
      }
    }
    
    // Translate program type for English
    let programTypeDisplayEn = programTypeDisplay;
    if (programTypeDisplay === 'Doctorat') programTypeDisplayEn = 'PhD';
    else if (programTypeDisplay === 'Licence') programTypeDisplayEn = 'Bachelor';
    else if (programTypeDisplay === 'HDR') programTypeDisplayEn = 'HDR';
    
    // Labels & Text
    const docTitleText = programTypeDisplay 
      ? (isFr ? `BULLETIN DE NOTES DE ${programTypeDisplay.toUpperCase()}` : `ACADEMIC TRANSCRIPT - ${programTypeDisplayEn.toUpperCase()}`)
      : (isFr ? "BULLETIN DE NOTES" : "ACADEMIC TRANSCRIPT");
    
    const labels = {
      docTitle: docTitleText,
      schoolTitle: isFr ? "ECOLE DOCTORALE" : "Doctoral School",
      schoolSub: isFr
        ? "Cotutelle Internationale de Thèse et Co-diplomation"
        : "International joint supervision of a thesis and joint award of a degree",
      uniTitle: isFr ? "UNIVERSITE DE LISALA" : "UNIVERSITY OF LISALA",
      center: isFr
        ? "Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)"
        : "Interuniversity Center for Multidisciplinary Research-CIREP",
      status: isFr ? "Status: Universite publique" : "Status: Government University",
      web: isFr ? "Web: www.universitedelisala.ac.cd" : "Web: https://universitedelisala.ac.cd/",
      email: isFr ? "Email: admission@universitedelisala.ac.cd" : "Email: info@universitedelisala.ac.cd",

      name: isFr ? "Nom" : "Name",
      matricule: isFr ? "Matricule" : "Registration Number",
      faculty: isFr ? "Faculté" : "Faculty",
      dept: isFr ? "Département" : "Department",
      tableYear: isFr ? "Année Academique" : "Academic Year",
      tableCode: "Code",
      tableTitle: isFr ? "Titre des cours" : "Course Title",
      tableCredit: isFr ? "Crédit" : "Credit",
      tableGrade: isFr ? "Notes/100" : "Grade/100",
      tableGPA: "GPA",
      total: isFr ? "Total" : "Total",
      semesterLabel: isFr ? "Semestre" : "Semester",
      generalTotal: isFr ? "TOTAL GÉNÉRAL" : "GENERAL TOTAL",
      verdictLabel: isFr ? "Verdict" : "Verdict",
      rectorName: "Professeur Abbé Donat TEBAKABE Alomo",
      rectorTitle: isFr ? "Recteur" : "Rector",
      dateLabel: formattedDate
    };

    // Verdict Translation
    let displayVerdict = data.verdict || '';
    if (isFr && displayVerdict) {
        if (displayVerdict === 'Promoted') displayVerdict = 'Admis';
        else if (displayVerdict === 'Rejected') displayVerdict = 'Ajourné';
        else if (displayVerdict === 'Pending') displayVerdict = 'En attente';
    }

    // Handle both old format (courses) and new format (semesters)
    let semesters = data.semesters;
    if (!semesters && data.courses) {
      // Migrate old format
      semesters = [{
        semesterNumber: 'I',
        courses: data.courses
      }];
    }

    if (!semesters || semesters.length === 0) {
      semesters = [{
        semesterNumber: 'I',
        courses: []
      }];
    }

    // Generate table rows grouped by semester
    let tableRows = '';
    let generalTotalCredits = '';
    let generalTotalGrades = '';
    let generalTotalGPA = '';

    semesters.forEach((semester, semIndex) => {
      const courses = semester.courses || [];
      if (courses.length === 0) return;

      const semesterTotalRows = courses.length + 1; // courses + total row

      // Capitalize semester number
      const capitalizedSemester = semester.semesterNumber ? semester.semesterNumber.toUpperCase() : 'I';
      
      // First course row with semester name
      const firstCourse = courses[0];
      // For English page, show only English title; for French, show French with English below
      const courseTitleDisplay = isFr 
        ? (firstCourse.titleEn 
            ? `${firstCourse.title || ''}<br/><small style="font-size: 10px; color: #666;">${firstCourse.titleEn}</small>`
            : firstCourse.title || '')
        : (firstCourse.titleEn || firstCourse.title || '');
      
      tableRows += `
        <tr>
          <td rowspan="${semesterTotalRows}" style="padding: 8px; text-align: center; border: 1px solid #000; vertical-align: middle; font-weight: bold;">
            ${labels.semesterLabel} ${capitalizedSemester}
          </td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${firstCourse.code || ''}</td>
          <td style="padding: 8px; text-align: left; border: 1px solid #000;">${courseTitleDisplay}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${firstCourse.credit || ''}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${firstCourse.grade || ''}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${firstCourse.gpa || '-'}</td>
        </tr>
      `;

      courses.slice(1).forEach((course) => {
        // For English page, show only English title; for French, show French with English below
        const courseTitleDisplay = isFr 
          ? (course.titleEn 
              ? `${course.title || ''}<br/><small style="font-size: 10px; color: #666;">${course.titleEn}</small>`
              : course.title || '')
          : (course.titleEn || course.title || '');
        tableRows += `
          <tr>
            <td style="padding: 8px; text-align: center; border: 1px solid #000;">${course.code || ''}</td>
            <td style="padding: 8px; text-align: left; border: 1px solid #000;">${courseTitleDisplay}</td>
            <td style="padding: 8px; text-align: center; border: 1px solid #000;">${course.credit || ''}</td>
            <td style="padding: 8px; text-align: center; border: 1px solid #000;">${course.grade || ''}</td>
            <td style="padding: 8px; text-align: center; border: 1px solid #000;">${course.gpa || '-'}</td>
          </tr>
        `;
      });

      // Total row for this semester (using manual totals)
      const totalCreditsDisplay = semester.totalCredits || '-';
      const totalGradesDisplay = semester.totalGrades || '-';
      const totalGPADisplay = semester.totalGPA || '-';
      
      tableRows += `
        <tr>
          <td colspan="2" style="padding: 8px; text-align: right; border: 1px solid #000;"><strong>${labels.total}</strong></td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${totalCreditsDisplay}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${totalGradesDisplay}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000;">${totalGPADisplay}</td>
        </tr>
      `;
    });

    // General Total Row
    const generalTotalCreditsDisplay = data.generalTotalCredits || '-';
    const generalTotalGradesDisplay = data.generalTotalGrades || '-';
    const generalTotalGPADisplay = data.generalTotalGPA || '-';
    
    if (generalTotalCreditsDisplay !== '-' || generalTotalGradesDisplay !== '-' || generalTotalGPADisplay !== '-') {
      tableRows += `
        <tr style="background-color: #f0f0f0;">
          <td colspan="3" style="padding: 8px; text-align: right; border: 1px solid #000;"><strong>${labels.generalTotal}</strong></td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000; font-weight: bold;">${generalTotalCreditsDisplay}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000; font-weight: bold;">${generalTotalGradesDisplay}</td>
          <td style="padding: 8px; text-align: center; border: 1px solid #000; font-weight: bold;">${generalTotalGPADisplay}</td>
        </tr>
      `;
    }

    return `
      <div class="transcript-page" style="position: relative; width: 21cm; min-height: 29.7cm; padding: 1.5cm; background: white; box-sizing: border-box; margin: 0 auto 30px auto; box-shadow: 0 0 15px rgba(0,0,0,0.2);">
        
        <div class="aquidedroit-header">
          <div class="aquidedroit-header-row" style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
            <div class="aquidedroit-left-logo" style="display: flex; flex-direction: column; align-items: center; width: 190px; flex: 0 0 190px; text-align: center;">
              <img src="${unilislogo}" alt="Logo UNILIS" style="width: 120px; height: 120px; object-fit: contain;" />
              <div class="aquidedroit-left-text" style="margin-top: 10px; text-align: center; width: 100%;">
                <h1 style="font-size: 11pt; font-weight: bold; margin: 0 0 3px 0; color: #000;">${labels.schoolTitle}</h1>
                <h2 style="font-size: ${isFr ? '9pt' : '8pt'}; font-weight: bold; margin: 0; color: #000; line-height: 1.25;">${labels.schoolSub}</h2>
              </div>
            </div>
            <div class="aquidedroit-header-center" style="text-align: center; flex: 1; min-width: 0; margin: 0 12px;">
              <h3 style="font-size: 18pt; font-weight: bold; margin: 0 0 10px 0; color: #000;">${labels.uniTitle}</h3>
              <p style="font-size: 12pt; margin: 5px 0; color: #000;">${labels.center}</p>
              <p style="font-size: 11pt; margin: 3px 0; color: #000;">${labels.status}</p>
              <p style="font-size: 11pt; margin: 3px 0; color: #000;">${labels.web}</p>
              <p style="font-size: 11pt; margin: 3px 0; color: #000;">${labels.email}</p>
            </div>
            <div class="aquidedroit-right-logo" style="width: 120px; flex: 0 0 120px; text-align: center;">
              <img src="${logo}" alt="Logo EDAP" style="width: 120px; height: 120px; object-fit: contain;" />
            </div>
          </div>
        </div>
        <div style="width: 100%; height: 2px; background-color: #000; margin: 20px 0;"></div>

        <!-- Document Title -->
        <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="font-size: 20pt; font-weight: bold; margin: 0; color: #000; text-transform: uppercase;">${labels.docTitle}</h1>
        </div>

        <!-- Student Info -->
        <div class="student-info" style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%; margin-top: 20px;">
          <div class="stud-info" style="text-align: left; width: 90%;">
            <p style="margin: 5px 0;"><strong>${labels.name}:</strong> ${data.name}</p>
            <p style="margin: 5px 0;"><strong>${labels.matricule}:</strong> ${data.matriculationNumber}</p>
            <p style="margin: 5px 0;"><strong>${labels.faculty}:</strong> ${data.faculty}</p>
            <p style="margin: 5px 0;"><strong>${labels.dept}:</strong> ${data.department}</p>
            <p style="margin: 5px 0;"><strong>${labels.tableYear}:</strong> ${data.year || ''}</p>
          </div>
          <div class="stud-img" style="display: flex; justify-content: flex-end;">
            ${data.image ? `<img src="${data.image}" alt="Student" style="width: 100px; height: 100px; object-fit: cover; border: 1px solid #ddd;" />` : ''}
          </div>
        </div>

        <!-- Table -->
        <table class="transcript-table" style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px;">
          <thead>
            <tr>
              <th style="padding: 8px; text-align: center; border: 1px solid #000;">${labels.semesterLabel}</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #000;">${labels.tableCode}</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #000;">${labels.tableTitle}</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #000;">${labels.tableCredit}</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #000;">${labels.tableGrade}</th>
              <th style="padding: 8px; text-align: center; border: 1px solid #000;">${labels.tableGPA}</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <!-- Verdict and Date Section -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 20px; font-size: 14px;">
          <div style="text-align: left;">
            ${displayVerdict ? `<p style="margin: 5px 0;"><strong>${labels.verdictLabel}:</strong> ${displayVerdict.toUpperCase()}</p>` : ''}
          </div>
          <div style="text-align: right;">
            ${labels.dateLabel ? `<p style="margin: 5px 0;">${labels.dateLabel}</p>` : ''}
          </div>
        </div>

        <!-- Signature & QR -->
        <div class="aquidedroit-signature" style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; padding: 0 10px;">
          <div class="aquidedroit-signature-content" style="text-align: left;">
            <img src="${rectorSig}" alt="Signature" style="width: 120px; height: 60px; object-fit: contain; margin-bottom: 10px;" />
            <p style="font-size: 12pt; font-weight: bold; margin: 5px 0; color: #000;">${labels.rectorName}</p>
            <p style="font-size: 11pt; margin: 0; color: #000;">${labels.rectorTitle}</p>
          </div>
          ${qrCodeData ? `
          <div class="aquidedroit-qr-section" style="display: flex; align-items: center; justify-content: center;">
            <img src="${qrCodeData}" alt="QR Code" style="width: 80px; height: 80px; border: 1px solid #000;" />
          </div>` : ''}
        </div>

      </div>
    `;
  };

  // Main generator function (Async)
  const generateTranscriptHTML = async (data) => {
    // 1. Generate QR Code
    const qrText = `UNCR TRANSCRIPT\nName: ${data.name}\nMatricule: ${data.matriculationNumber}\nFaculty: ${data.faculty}\nYear: ${data.year}\nVerified by UNCR`;
    let qrCodeData = '';
    try {
      qrCodeData = await QRCode.toDataURL(qrText, { width: 100, margin: 1 });
    } catch (err) {
      console.error('QR Gen Error', err);
    }

    // 2. Generate Pages
    const frenchPage = generatePage(data, 'fr', qrCodeData);
    const englishPage = generatePage(data, 'en', qrCodeData);

    // Return the content directly without html/body tags for better compatibility with html2pdf
    return `
      <style>
        .transcript-wrapper-doc {
            display: flex;
            flex-direction: column;
            align-items: center;
            background-color: #525659;
            padding: 40px;
            width: fit-content;
            min-width: 22cm; /* Ensure slightly larger than page */
            margin: 0 auto;
        }
        .transcript-page {
            position: relative; 
            width: 21cm; 
            min-height: 29.7cm; 
            padding: 2cm; 
            background: white; 
            box-sizing: border-box; 
            margin: 0 auto 30px auto; 
            box-shadow: 0 0 15px rgba(0,0,0,0.2);
        }
        
        /* Print specific styles */
        @media print {
            .transcript-wrapper-doc {
                background: none;
                padding: 0;
                width: 100%;
                display: block; /* block layout for print flow */
            }
            .transcript-page {
                box-shadow: none !important;
                margin: 0 !important;
                page-break-after: always; 
                width: 100%;
                min-height: 29.7cm; /* ensure full height */
            }
            .transcript-page:last-child {
                page-break-after: avoid;
            }
        }
        
        /* Ensure styles from generatePage are supported if not inline */
        .aquidedroit-header { margin-bottom: 20px; }
        .aquidedroit-header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
        .aquidedroit-separator { width: 100%; height: 2px; background-color: #000; margin: 20px 0; }
        .transcript-table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
        .aquidedroit-signature { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; padding: 0 10px; }
      </style>
      <div class="transcript-wrapper-doc">
        ${frenchPage}
        ${englishPage}
      </div>
    `;
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [selectedTranscript, setSelectedTranscript] = useState(null);

  const withStudentPhoto = async (transcript) => {
    if (transcript.image || !(transcript.id || transcript.matriculationNumber)) {
      return transcript;
    }
    const photoKey = transcript.id || transcript.matriculationNumber;
    try {
      const image = await fetchTranscriptField(transcriptRootUrl, photoKey, 'image');
      if (!image) return transcript;
      const withImage = { ...transcript, image };
      setTranscripts((prev) => prev.map((item) => (
        item.id === photoKey ? { ...item, image } : item
      )));
      return withImage;
    } catch (error) {
      console.error('Error loading student photo:', error);
      return transcript;
    }
  };

  const handleTranscriptClick = async (transcript) => {
    setSelectedTranscript(transcript);
    setModalContent('');
    setIsModalLoading(true);
    setIsModalOpen(true);

    try {
      const fullTranscript = await withStudentPhoto(transcript);
      const freshHtml = await generateTranscriptHTML(fullTranscript);
      setSelectedTranscript(fullTranscript);
      setModalContent(freshHtml);
    } catch (error) {
      console.error('Error opening bulletin:', error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsModalLoading(false);
    setSelectedTranscript(null);
    setModalContent('');
  };

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
        setSelectedTranscript(null);
        setModalContent('');
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isModalOpen]);

  const handleDeleteTranscript = (matriculationNumber) => {
    hiddenIdsRef.current.add(matriculationNumber);
    setTranscripts((prev) => prev.filter((item) => (
      item.id !== matriculationNumber && item.matriculationNumber !== matriculationNumber
    )));
    const transcriptRef = ref(db, `/transcripts/UNCR/${matriculationNumber}`);
    remove(transcriptRef)
      .then(() => console.log(`Transcript with Matricule ${matriculationNumber} deleted successfully`))
      .catch((error) => console.error('Error deleting transcript:', error));
  };

  const handleDownloadPDF = async (transcript) => {
    const data = await withStudentPhoto(transcript);
    const freshHtml = await generateTranscriptHTML(data);
    
    // Create temporary container
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.top = '-10000px';
    tempContainer.style.left = '-10000px';
    tempContainer.style.zIndex = '-1000';
    tempContainer.innerHTML = freshHtml;
    document.body.appendChild(tempContainer);

    try {
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pages = tempContainer.querySelectorAll('.transcript-page');

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        // Ensure white background for capture
        page.style.backgroundColor = 'white';

        // Wait for images to load
        const images = page.querySelectorAll('img');
        await Promise.all(Array.from(images).map(img => new Promise((resolve) => {
          if (img.complete) resolve();
          else { img.onload = resolve; img.onerror = resolve; }
        })));

        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#FFFFFF',
          windowWidth: page.scrollWidth,
          windowHeight: page.scrollHeight
        });

        // Calculate scaling
        const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
        const w = canvas.width * ratio;
        const h = canvas.height * ratio;
        const x = (pageWidth - w) / 2;
        const y = 20; // Slight top margin

        if (i > 0) pdf.addPage();
        
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
      }

      pdf.save(`${data.name.replace(/\s+/g, '_')}_Transcript.pdf`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
    } finally {
      document.body.removeChild(tempContainer);
    }
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredTranscripts = transcripts.filter((transcript) => {
    if (!normalizedSearch) return true;
    return [transcript.name, transcript.matriculationNumber, transcript.faculty, transcript.department]
      .some((value) => (value || '').toLowerCase().includes(normalizedSearch));
  });

  return (
    <div className="transcript-wrapper">
      <div className="form-blt">
        <img src={logo} className="form-logo" alt="Logo" />
        <form onSubmit={handleSubmit} className='trans-form' >
          {/* Form Inputs - Two Column Layout */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            {/* Left Column - 5 fields */}
            <div>
              <input
                type="text"
                name="name"
                placeholder="Nom"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <select
                name="programType"
                value={formData.programType}
                onChange={handleInputChange}
                required
                className="trans-form-select"
              >
                <option value="">-- Sélectionner le type de programme --</option>
                <option value="Licence">Licence</option>
                <option value="Master">Master</option>
                <option value="Doctorat">Doctorat</option>
                <option value="HDR">HDR</option>
              </select>
            </div>
            <div>
              <input
                type="text"
                name="faculty"
                placeholder="Faculté"
                value={formData.faculty}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <input
                type="text"
                name="department"
                placeholder="Département"
                value={formData.department}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <input
                type="text"
                name="year"
                placeholder="Année académique"
                value={formData.year}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <input
                type="text"
                name="matriculationNumber"
                placeholder="Matricule"
                value={formData.matriculationNumber}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            
            {/* Right Column - 5 fields */}
            <div>
              <input
                type="text"
                name="class"
                placeholder="Classe"
                value={formData.class}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <select
                  name="verdict"
                  value={formData.verdict}
                  onChange={handleInputChange}
                  required
                  className="trans-form-select"
              >
                  <option value="">-- Sélectionner le verdict --</option>
                  <option value="Promoted">Admis</option>
                  <option value="Rejected">Ajourné</option>
                  <option value="Pending">En attente</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                className="trans-form-input"
              />
            </div>
            <div>
              <input
                type="file"
                onChange={handleImageChange}
                className="trans-form-input"
              />
            </div>
          </div>

          {/* Semesters and Courses */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '15px' }}>
              {isEditing ? 'Modifier le Bulletin' : 'Créer le Bulletin'}
            </h3>
            {formData.semesters.map((semester, semesterIndex) => (
              <div key={semesterIndex} style={{ 
                border: '2px solid #ddd', 
                borderRadius: '8px', 
                padding: '15px', 
                marginBottom: '20px',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ margin: 0, color: '#000' }}>
                    Semestre {capitalizeRomanNumeral(semester.semesterNumber)}
                  </h4>
                  {formData.semesters.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => removeSemester(semesterIndex)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Supprimer Semestre
                    </button>
                  )}
                </div>
                
                {semester.courses.map((course, courseIndex) => (
                  <div key={courseIndex} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        name="code"
                        placeholder="Code"
                        value={course.code}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        required
                        className="trans-form-input"
                        style={{ width: '150px' }}
                      />
                      <input
                        type="text"
                        name="title"
                        placeholder="Titre du cours"
                        value={course.title}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        required
                        className="trans-form-input"
                        style={{ flex: 1, minWidth: '200px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
                      <input
                        type="text"
                        name="titleEn"
                        placeholder="Course title"
                        value={course.titleEn || ''}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        className="trans-form-input"
                        style={{ flex: 1, minWidth: '200px' }}
                      />
                      <input
                        type="number"
                        name="credit"
                        placeholder="Crédit"
                        value={course.credit}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        required
                        className="trans-form-input"
                        style={{ width: '100px' }}
                      />
                      <input
                        type="number"
                        name="grade"
                        placeholder="Note/100"
                        value={course.grade}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        required
                        className="trans-form-input"
                        style={{ width: '120px' }}
                      />
                      <input
                        type="number"
                        name="gpa"
                        placeholder="GPA"
                        value={course.gpa}
                        onChange={(e) => handleCourseChange(semesterIndex, courseIndex, e)}
                        step="0.01"
                        className="trans-form-input"
                        style={{ width: '100px' }}
                      />
                      {semester.courses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCourse(semesterIndex, courseIndex)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Semester Totals */}
                <div style={{ 
                  marginTop: '15px', 
                  padding: '10px', 
                  backgroundColor: '#e9ecef', 
                  borderRadius: '5px',
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}>
                  <strong style={{ marginRight: '10px' }}>Totaux du Semestre:</strong>
                  <input
                    type="text"
                    name="totalCredits"
                    placeholder="Total Crédits (ex: 24/30)"
                    value={semester.totalCredits || ''}
                    onChange={(e) => handleSemesterTotalChange(semesterIndex, e)}
                    className="trans-form-input"
                    style={{ width: '150px' }}
                  />
                  <input
                    type="number"
                    name="totalGrades"
                    placeholder="Total Notes"
                    value={semester.totalGrades || ''}
                    onChange={(e) => handleSemesterTotalChange(semesterIndex, e)}
                    className="trans-form-input"
                    style={{ width: '120px' }}
                  />
                  <input
                    type="number"
                    name="totalGPA"
                    placeholder="Total GPA"
                    value={semester.totalGPA || ''}
                    onChange={(e) => handleSemesterTotalChange(semesterIndex, e)}
                    step="0.01"
                    className="trans-form-input"
                    style={{ width: '100px' }}
                  />
                </div>
                
                <button 
                  type="button" 
                  onClick={() => addCourse(semesterIndex)}
                  className='trans-form-submit-btn'
                  style={{ marginTop: '10px', width: 'auto', minWidth: '150px' }}
                >
                  Ajouter un cours
                </button>
              </div>
            ))}
            
            {/* General Total */}
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '8px',
              border: '2px solid #2196f3'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#000' }}>Total Général</h4>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  name="generalTotalCredits"
                  placeholder="Total Crédits (ex: 120/120)"
                  value={formData.generalTotalCredits || ''}
                  onChange={handleInputChange}
                  className="trans-form-input"
                  style={{ width: '200px' }}
                />
                <input
                  type="number"
                  name="generalTotalGrades"
                  placeholder="Total Notes"
                  value={formData.generalTotalGrades || ''}
                  onChange={handleInputChange}
                  className="trans-form-input"
                  style={{ width: '150px' }}
                />
                <input
                  type="number"
                  name="generalTotalGPA"
                  placeholder="Total GPA"
                  value={formData.generalTotalGPA || ''}
                  onChange={handleInputChange}
                  step="0.01"
                  className="trans-form-input"
                  style={{ width: '150px' }}
                />
              </div>
            </div>
            
            <button 
              type="button" 
              onClick={addSemester}
              className='trans-form-submit-btn'
              style={{ marginTop: '10px', backgroundColor: '#28a745' }}
            >
              Ajouter un Semestre
            </button>
            
            <button 
              className='trans-form-submit-btn' 
              type="submit"
              style={{ marginTop: '10px' }}
            >
              {isEditing ? 'Mettre à jour le Bulletin' : 'Générer le Bulletin'}
            </button>
            
            {isEditing && (
              <button 
                type="button"
                onClick={resetForm}
                className='trans-form-submit-btn'
                style={{ 
                  marginTop: '10px', 
                  marginLeft: '10px',
                  backgroundColor: '#6c757d' 
                }}
              >
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="transcript-output">
        <h3 style={{ fontFamily: "monospace" }}>Bulletin disponible</h3>
        <div className="bulletin-search-bar">
          <input
            type="text"
            placeholder="Rechercher par nom ou matricule..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {isLoadingList && (
          <p className="bulletin-loading">
            <span className="bulletin-loading-dot" />
            {totalCount == null
              ? 'Chargement des bulletins…'
              : `Chargement des bulletins… ${loadedCount} sur ${totalCount}`}
          </p>
        )}
        {listError && <p className="bulletin-list-message">{listError}</p>}
        {!isLoadingList && filteredTranscripts.length === 0 && !listError && (
          <p className="bulletin-list-message">
            {transcripts.length === 0 ? 'Aucun bulletin disponible.' : 'Aucun bulletin trouvé.'}
          </p>
        )}
        <div className="bulletin-list">
          {filteredTranscripts.map((transcript) => (
            <div className="name-space" key={transcript.id || transcript.matriculationNumber}>
              <div>
                <button onClick={() => handleTranscriptClick(transcript)} className="name-tag">
                  {transcript.name}
                </button>
              </div>
              <div>
                <button
                  onClick={() => handleEditTranscript(transcript)}
                  className="crud-btn"
                  style={{ backgroundColor: '#007bff', color: 'white', border: '1px solid #007bff' }}
                >
                  Modifier
                </button>
                <Popconfirm
                  title="Supprimer le bulletin"
                  description="Êtes-vous sûr de vouloir supprimer ce bulletin ?"
                  onConfirm={() => handleDeleteTranscript(transcript.matriculationNumber || transcript.id)}
                  okText="Oui"
                  cancelText="Non"
                  okButtonProps={{ danger: true }}
                >
                  <button type="button" className="crud-btn crud-btn-delete">
                    Supprimer
                  </button>
                </Popconfirm>
                <button
                  onClick={() => handleDownloadPDF(transcript)}
                  className="crud-btn"
                >
                  Telecharger
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ConfigProvider>
        <Modal
          title={
            <span style={{ color: 'white' }}>
              {selectedTranscript ? `Aperçu - ${selectedTranscript.name}` : 'Aperçu du Bulletin'}
            </span>
          }
          open={isModalOpen}
          onCancel={handleModalClose}
          width="95vw"
          style={{ top: 10 }}
          className="bulletin-modal"
          centered={false}
          maskClosable={true}
          keyboard={true}
          footer={[
            <Button
              key="edit"
              onClick={() => {
                if (selectedTranscript) {
                  handleEditTranscript(selectedTranscript);
                  handleModalClose();
                }
              }}
              style={{ fontWeight: 'bold' }}
            >
              Modifier
            </Button>,
            <Button
              key="download"
              type="primary"
              icon={<Download size={16} />}
              onClick={() => {
                if (selectedTranscript) {
                  handleDownloadPDF(selectedTranscript);
                }
              }}
              style={{
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                fontWeight: 'bold'
              }}
            >
              Télécharger PDF
            </Button>,
            <Button
              key="close"
              onClick={handleModalClose}
              style={{ fontWeight: 'bold' }}
            >
              Fermer
            </Button>
          ]}
        >
          {selectedTranscript && (
            <div className="bulletin-modal-content">
              {isModalLoading ? (
                <p className="bulletin-loading">Chargement du bulletin…</p>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: modalContent }} />
              )}
            </div>
          )}
        </Modal>
      </ConfigProvider>

    </div>
  );
};

export default Transcript;
